import { useCallback, useEffect, useState } from "react";

const STORAGE_KEY = "paisacx.theme";
const EVENT = "paisacx:theme";

function readTheme() {
  if (typeof window === "undefined") return "dark";
  try {
    return localStorage.getItem(STORAGE_KEY) === "light" ? "light" : "dark";
  } catch {
    return "dark";
  }
}

function applyTheme(theme) {
  const el = document.documentElement;
  el.classList.remove("dark", "light");
  el.classList.add(theme);
}

/** The admin console is designed for the dark palette only; restores the visitor's theme on exit. */
export function useForceDarkTheme() {
  useEffect(() => {
    applyTheme("dark");
    return () => applyTheme(readTheme());
  }, []);
}

/** Dark is the default PaisaCX theme; the choice persists per browser. */
export function useTheme() {
  const [theme, setTheme] = useState(readTheme);

  useEffect(() => {
    const sync = () => setTheme(readTheme());
    window.addEventListener(EVENT, sync);
    window.addEventListener("storage", sync);
    return () => {
      window.removeEventListener(EVENT, sync);
      window.removeEventListener("storage", sync);
    };
  }, []);

  const toggleTheme = useCallback(() => {
    const next = readTheme() === "dark" ? "light" : "dark";
    try {
      localStorage.setItem(STORAGE_KEY, next);
    } catch {
      /* storage unavailable — still switch for this page view */
    }
    applyTheme(next);
    window.dispatchEvent(new Event(EVENT));
  }, []);

  return { theme, toggleTheme };
}
